import React, { useState, useCallback, useEffect } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import { FileText, X } from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";

export const WordNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [fileName, setFileName] = useState<string>(
    data?.file_name || "Report.docx",
  );
  const [prompt, setPrompt] = useState<string>(data?.prompt || "");
  const [content, setContent] = useState<string>(data?.content || "");
  const [summary, setSummary] = useState<string>(data?.summary || "");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes } = useReactFlow();

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;
  const isConfigured = !!(fileName && prompt && content);

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id !== id
          ? node
          : {
              ...node,
              data: { ...node.data, file_name: fileName, prompt, content, summary },
            },
      ),
    );
  }, [fileName, prompt, content, summary, id, setNodes]);

  const handleDrop = (e: React.DragEvent, field: string) => {
    e.preventDefault();
    e.stopPropagation();
    let variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      variable = variable.replace(/^{{|}}$/g, "");
      if (field === "content") setContent(variable);
      if (field === "summary") setSummary(variable);
      if (field === "prompt") setPrompt((prev) => prev + (prev ? " " : "") + variable);
    }
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  // ── Custom outputs sidebar (URL rendering) ────────────────────────
  const renderOutputsSidebar = useCallback(
    () => (
      <div className="w-80 bg-gray-50 overflow-y-auto">
        <div className="p-4">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              Node Outputs
            </h3>
            <p className="text-xs text-gray-500">Available after execution</p>
          </div>
          {hasOutputs ? (
            <div className="space-y-3">
              {Object.entries(data.outputs).map(([key, value]: [string, any]) => (
                <div
                  key={key}
                  className="bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      <span className="text-sm font-semibold text-gray-900">{key}</span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">
                      {typeof value}
                    </span>
                  </div>
                  {key === "url" && typeof value === "string" ? (
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all font-mono flex items-center gap-1"
                    >
                      {value}
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </a>
                  ) : (
                    <pre
                      className="text-xs font-mono break-all whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: syntaxHighlight(value) }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 font-medium mb-1">No document generated</p>
            </div>
          )}
        </div>
      </div>
    ),
    [hasOutputs, data.outputs],
  );

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Word Document"
      icon={<FileText className="w-4 h-4 text-blue-700" />}
      nodeDocsType="WORD"
      themeColor="blue"
      configureLabel="Configure Document"
      isConfigured={isConfigured}
      validationErrors={[
        ...(!fileName ? ["File name is required"] : []),
        ...(!content ? ["Content source is required"] : []),
        ...(!prompt ? ["Generation prompt is required"] : []),
      ]}
      statusText={{
        running: "Generating...",
        success: "Doc Generated",
        error: "Generation Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Word Document - Generate .docx"
        icon={<FileText className="w-5 h-5 text-blue-600" />}
        themeColor="blue"
        nodeDocsType="WORD"
        nodeId={id}
        validationStatus={{
          isValid: isConfigured,
          errors: [
            ...(!fileName ? ["File name is required"] : []),
            ...(!content ? ["Content source is required"] : []),
            ...(!prompt ? ["Generation prompt is required"] : []),
          ],
        }}
        nodeData={data}
        nodeIcon={<FileText className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No document generated"
        renderOutputsSidebar={renderOutputsSidebar}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Document Configuration
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Define the content sources and prompt for generation
        </p>

        <div className="space-y-6">
          {/* File Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Output File Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="e.g. Project_Status_Report.docx"
              className={`w-full px-3 py-2 border ${!fileName ? "border-red-300" : "border-gray-300"} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none`}
            />
            {!fileName && (
              <p className="text-xs text-red-500 mt-1">File name is required</p>
            )}
          </div>

          {/* Content Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Content Source <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "content")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center justify-between ${
                content ? "border-blue-300 bg-blue-50/30" : "border-gray-300 bg-gray-50"
              }`}
            >
              <input
                type="text"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Drag content here (e.g. {{LLMNode.outputs.content}})"
                className="w-full bg-transparent border-none outline-none text-gray-700 placeholder:text-gray-400"
              />
              {content && (
                <button onClick={() => setContent("")} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {!content && (
              <p className="text-xs text-red-500 mt-1">Content source is required</p>
            )}
          </div>

          {/* Summary Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Summary Source <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "summary")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center justify-between ${
                summary ? "border-blue-300 bg-blue-50/30" : "border-gray-300 bg-gray-50"
              }`}
            >
              <input
                type="text"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Drag summary data here (e.g. {{LLMNode.outputs.summary}})"
                className="w-full bg-transparent border-none outline-none text-gray-700 placeholder:text-gray-400"
              />
              {summary && (
                <button onClick={() => setSummary("")} className="text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Generation Prompt <span className="text-red-500">*</span>
            </label>
            <div
              onDrop={(e) => handleDrop(e, "prompt")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`p-1 rounded-lg border ${
                isDraggingOver ? "border-blue-500 bg-blue-50" : "border-transparent"
              }`}
            >
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
                placeholder="Describe how to format the document..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-y"
              />
            </div>
            {!prompt && (
              <p className="text-xs text-red-500 mt-1">Generation prompt is required</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Instructions for the AI on how to structure and fill the Word document.
            </p>
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default WordNode;
